/**
 * H2 / H2-F — Holder Utilities: benefits grouped by Kickback with status/Kickback filters.
 * Kickbacks without benefits are collapsed so they don't bury real rights.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift } from 'lucide-react';
import theme from '../theme';
import BenefitList from '../components/holder/BenefitList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [['all', 'All'], ['available', 'Available'], ['locked', 'Locked'], ['scheduled', 'Upcoming'], ['redeemed', 'Redeemed'], ['ended', 'Ended / not set up']];
const ORDER = { available: 0, locked: 1, scheduled: 2, redeemed: 3, not_configured: 4, sold_out: 5, expired: 6 };
const matches = (b, f) => f === 'all' || b.status === f || (f === 'ended' && ['expired', 'sold_out', 'not_configured'].includes(b.status));
const chip = (on) => ({ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${on ? theme.colors.primary : theme.colors.borders}`, background: on ? theme.colors.primary : '#fff', color: on ? '#fff' : theme.colors.text.primary });
const Tag = ({ children, tone }) => <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: tone === 'warn' ? '#FFFBEB' : '#ECFDF5', color: tone === 'warn' ? '#92400E' : '#065F46', textTransform: 'uppercase' }}>{children}</span>;

export default function BuyerUtilitiesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all');
  const [kb, setKb] = useState('all');
  const [showEmpty, setShowEmpty] = useState(false);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/buyer/benefits`, auth)).data.kickbacks || []); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not load your benefits'); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const withBenefits = (rows || []).filter(k => k.benefits.length > 0);
  const empty = (rows || []).filter(k => k.benefits.length === 0);
  const visible = withBenefits
    .filter(k => kb === 'all' || k.portfolio_id === kb)
    .map(k => ({ ...k, shown: k.benefits.filter(b => matches(b, filter)).sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9)) }))
    .filter(k => k.shown.length > 0);

  return (
    <div data-testid="buyer-utilities-page" style={{ padding: '24px 28px', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
        <Gift size={22} color={theme.colors.primary} />
        <h1 style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary }}>My Utilities</h1>
      </div>
      <div style={{ fontSize: 13, color: theme.colors.text.muted, marginBottom: 16 }}>
        Benefits from Kickbacks you've claimed, bought, or hold as an NFT. Each creator decides when their benefits unlock. You don't need an NFT for benefits a creator gives to claimants.
      </div>
      {err && <div data-testid="utilities-error" style={{ padding: 12, background: '#FEF2F2', color: '#991B1B', borderRadius: 8 }}>{err}</div>}
      {!rows && !err && <div data-testid="utilities-loading">Loading…</div>}
      {rows && withBenefits.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {FILTERS.map(([k, l]) => <button key={k} data-testid={`utilities-filter-${k}`} style={chip(filter === k)} onClick={() => setFilter(k)}>{l}</button>)}
          <select data-testid="utilities-kickback-filter" value={kb} onChange={e => setKb(e.target.value)} style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 8, border: `1px solid ${theme.colors.borders}`, fontSize: 12 }}>
            <option value="all">All Kickbacks</option>
            {withBenefits.map(k => <option key={k.portfolio_id} value={k.portfolio_id}>{k.portfolio_name} · {k.creator_name}</option>)}
          </select>
        </div>
      )}
      {rows && withBenefits.length === 0 && (
        <div data-testid="utilities-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted, border: `1px dashed ${theme.colors.borders}`, borderRadius: 12, marginBottom: 14 }}>
          {rows.length === 0 ? "You're not connected to any Kickback yet. Claim a Kickback link from a creator to get started." : "None of your Kickbacks have benefits attached yet."}
          <div style={{ marginTop: 12 }}>
            <button data-testid="utilities-go-claims-btn" onClick={() => navigate('/buyer/claims')} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Go to My Claims</button>
          </div>
        </div>
      )}
      {rows && withBenefits.length > 0 && visible.length === 0 && <div data-testid="utilities-filter-empty" style={{ fontSize: 13, color: theme.colors.text.muted, marginBottom: 14 }}>No benefits match this filter.</div>}
      {visible.map(k => (
        <div key={k.portfolio_id} data-testid={`utilities-kickback-${k.portfolio_id}`} style={{ background: '#fff', border: `1px solid ${theme.colors.borders}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontWeight: 800, color: theme.colors.text.primary, flex: 1 }}>{k.portfolio_name || 'Kickback'} <span style={{ fontWeight: 500, fontSize: 12, color: theme.colors.text.muted }}>by {k.creator_name}</span></div>
            {k.has_entitlement && <Tag>Claimed</Tag>}
            {k.purchase_verified && <Tag>Verified purchase</Tag>}
            {k.purchase_unverified && <Tag tone="warn">Purchase record · unverified</Tag>}
            {k.has_verified_nft && <Tag>Verified NFT</Tag>}
          </div>
          <BenefitList benefits={k.shown} portfolioId={k.portfolio_id} onRedeemed={load} />
        </div>
      ))}
      {empty.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button data-testid="utilities-toggle-empty" onClick={() => setShowEmpty(!showEmpty)} style={{ background: 'none', border: 'none', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {showEmpty ? 'Hide' : 'Show'} {empty.length} other Kickback(s) with no benefits attached
          </button>
          {showEmpty && (
            <div data-testid="utilities-empty-list" style={{ fontSize: 13, color: theme.colors.text.secondary, marginTop: 6 }}>
              {empty.map(k => <div key={k.portfolio_id} style={{ padding: '4px 0' }}>{k.portfolio_name || 'Kickback'} · {k.creator_name}{k.has_entitlement ? ' · claimed' : ''}{k.purchase_unverified ? ' · purchase record (unverified)' : ''}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
